/**
 * /ndt-inspection/tickets — the NDT field-ticket register
 * (GET /ndt-inspection/tickets, fieldiq-api#1157). Full width; a row opens the
 * ticket on its own page. Status, scope, job and the date range live in the
 * URL so the record's back arrow returns to the list as it was left.
 *
 * Status, job and dates are the API's filters (`status`, `jobId`, `from`,
 * `to` — a day ticket's work day, else its local creation day). Scope has no
 * API filter, so it narrows the loaded rows by the scope the API sent.
 *
 * CHIP AND BAND COUNTS are tallies of the UNFILTERED list — honest because the
 * endpoint is unpaginated and no endpoint returns per-status counts. A
 * subtotal the API holds as NULL reads "No rate", never $0.
 *
 * Tickets are made from a JOB (closing a day, or completing it), so the header
 * action leads to the jobs.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileClock, PenLine } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useNdtJobs } from '../../hooks/useNdtInspection';
import { useNdtTickets } from '../../hooks/useNdtTickets';
import { queryLoadFailure } from '../../lib/load-failure';
import { NDT_TICKET_STATUS_SKIN, ndtTicketStatusSkin } from '../../lib/ndt-tickets';
import { NDT_TICKET_SCOPES, NDT_TICKET_STATUSES, type NdtTicket } from '../../types/ndt-tickets';
import { useOpenRecord } from '../downhole/recordPage';
import { NDT_DATE_INPUT, NDT_INPUT, NdtChip, NdtChipRow, NdtEmpty, NdtField, NdtPanel, NdtRegister } from './NdtBits';
import { NdtTicketCard, ndtTicketColumns } from './NdtTicketBits';

const STATUS_CHIPS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  ...NDT_TICKET_STATUSES.map((s) => ({ key: s, label: NDT_TICKET_STATUS_SKIN[s].label })),
];
const SCOPE_LABEL: Record<string, string> = { day: 'Day tickets', job: 'Whole-job tickets', final: 'Final tickets' };

export default function TicketsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const scope = params.get('scope') ?? '';
  const jobId = params.get('job') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const listQuery = useNdtTickets({ status: status || undefined, jobId: jobId || undefined, from: from || undefined, to: to || undefined });
  const allQuery = useNdtTickets();
  const jobs = useNdtJobs();
  const tz = (allQuery.data?.meta ?? listQuery.data?.meta)?.timeZone ?? null;
  const rows = (listQuery.data?.data ?? []).filter((t) => !scope || t.scope === scope);
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (t: NdtTicket) => boolean) => (all ? all.filter(pick).length : undefined);
  const filtered = Boolean(status || scope || jobId || from || to);

  return (
    <div data-testid="ndt-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="Field Tickets"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button type="button" onClick={() => navigate('/ndt-inspection/jobs')} className="inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10">
            Jobs →
          </button>
        }
        stats={[
          { icon: <FileClock className="size-4" aria-hidden="true" />, label: 'Drafts', sub: 'To be signed', tone: 'accent', loading: allQuery.isLoading, value: displayValue(countOf((t) => t.status === 'draft')) },
          { icon: <PenLine className="size-4" aria-hidden="true" />, label: 'Signed', sub: 'To approve', tone: 'accent', loading: allQuery.isLoading, value: displayValue(countOf((t) => t.status === 'signed')) },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Approved', sub: 'Billable', tone: 'success', loading: allQuery.isLoading, value: displayValue(countOf((t) => t.status === 'approved')) },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Unpriced',
            sub: 'Drafts, no rate',
            tone: 'accent',
            loading: allQuery.isLoading,
            // The API's NULL subtotal on a draft with lines: a line it holds no price for.
            value: displayValue(countOf((t) => t.status === 'draft' && t.subtotal === null && t.lineCount > 0)),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">A ticket is drafted from its job. The company man signs it in the field; the office approves it for billing.</p>
          <NdtChipRow label="Filter by status">
            {STATUS_CHIPS.map((c) => (
              <NdtChip
                key={c.key || 'all'}
                label={c.label}
                active={status === c.key}
                count={all ? countOf((t) => !c.key || t.status === c.key) : undefined}
                onClick={() => setParam('status', c.key)}
              />
            ))}
          </NdtChipRow>
          <div className="grid gap-3 md:grid-cols-[200px_260px_180px_180px]">
            <NdtField label="Covers">
              <select aria-label="Scope" className={NDT_INPUT} value={scope} onChange={(e) => setParam('scope', e.target.value)} data-testid="ndt-tickets-scope">
                <option value="">All tickets</option>
                {NDT_TICKET_SCOPES.map((s) => (
                  <option key={s} value={s}>
                    {SCOPE_LABEL[s]}
                  </option>
                ))}
              </select>
            </NdtField>
            <NdtField label="Job">
              <select aria-label="Job" className={NDT_INPUT} value={jobId} onChange={(e) => setParam('job', e.target.value)} data-testid="ndt-tickets-job">
                <option value="">All jobs</option>
                {(jobs.data?.data ?? []).map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.jobNumber}
                    {j.customerName ? ` · ${j.customerName}` : ''}
                  </option>
                ))}
              </select>
            </NdtField>
            <NdtField label="From">
              <input type="date" aria-label="From" className={NDT_DATE_INPUT} value={from} onChange={(e) => setParam('from', e.target.value)} data-testid="ndt-tickets-from" />
            </NdtField>
            <NdtField label="To">
              <input type="date" aria-label="To" className={NDT_DATE_INPUT} value={to} onChange={(e) => setParam('to', e.target.value)} data-testid="ndt-tickets-to" />
            </NdtField>
          </div>

          <NdtPanel
            eyebrow="Charges signed in the field"
            title="Field tickets"
            testId="ndt-ticket-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && rows.length > 0
                ? filtered && all
                  ? `Showing ${rows.length} of ${all.length} tickets`
                  : `${rows.length} ticket${rows.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <NdtRegister
              query={listQuery}
              rows={rows}
              columns={ndtTicketColumns(tz)}
              rail={(t) => ndtTicketStatusSkin(t.status).rail}
              onOpen={(t) => openRecord(`/ndt-inspection/tickets/${t.id}`)}
              subject="field tickets"
              testId="ndt-ticket"
              minWidth="min-w-[1040px]"
              empty={
                filtered ? (
                  <NdtEmpty>No ticket matches these filters.</NdtEmpty>
                ) : (
                  <NdtEmpty>There are no field tickets. A ticket is drafted when a day is closed out on a job, or when a per-job job is completed.</NdtEmpty>
                )
              }
              card={(t) => <NdtTicketCard t={t} tz={tz} />}
            />
          </NdtPanel>
        </div>
      </section>
    </div>
  );
}
